<template>
	<Dialog v-model:visible="isVisible" :closable="true" modal class="custom-header custom-footer">
		<template #header>
			<div class="flex">
				<EmployeePicture :picture="employee.picture" :size="58" :spinner="true" :name="employee.name" v-if="employee" />
				<div class="ml-3 flex flex-col">
					<div class="text-sm font-light mt-2 opacity-85">{{ t('setPresence.header') }}</div>
					<div class="text-3xl opacity-85" style="margin-top:-5px">{{ employee?.name }}</div>
				</div>
			</div>
		</template>

		<div class="flex justify-between items-center gap-2 mt-4 mb-4" v-if="employee">
			<Button
				class="state-box-item" variant="outlined"
				:tabindex="index"
				:class="{ unselected: employee?.presence?.state !== item.key }"
				v-for="(item, index) in states"
				:key="item.key"
				@click="selectState(item.key)"
			>
				<PresenceIcon :state="item.key" :size="48" />
				{{ item.label }}
			</Button>
		</div>

		<div v-if="employee?.presence">
			<div class="mt-4">{{ t('setPresence.message') }}</div>
			<InputText v-model="employee.presence.message" type="email" id="presenceMessage" size="small" :disabled="isSaving" fluid @keydown="keyDownHandler" maxlength="512" tabindex="20" />

			<div class="mt-4">{{ t('setPresence.location') }}</div>
			<InputText v-model="employee.presence!.location" type="email" id="presenceLocation" size="small" :disabled="isSaving" fluid @keydown="keyDownHandler" maxlength="512" tabindex="30" />

			<div class="mt-4">{{ t('setPresence.until') }}</div>
			<InputGroup>
				<InputGroupAddon>
					<Checkbox v-model="expire.enabled" :binary="true" size="small" />
				</InputGroupAddon>
				<DatePicker v-model="expire.date" :min-date="expire.min" :max-date="expire.max" :step-minute="10" :disabled="!expire.enabled" date-format="DD d. MM yy"
					tabindex="50" showIcon showButtonBar showTime hourFormat="24" iconDisplay="input" input-id="presenceExpire" size="small">
					<template #inputicon="slotProps">
						<div style="width:20px;margin-top:-4px">
							<GlyphIcon :icon="GlyphIconName['calendar']" size="22" color="var(--p-text-muted-color)" @click="slotProps.clickCallback" />
						</div>
					</template>
				</DatePicker>
			</InputGroup>
		</div>

		<template #footer>
			<Button severity="secondary" @click="close()" tabindex="100" class="w-36"><span>{{ t('common.cancel') }}</span></Button>
			<Button severity="primary" @click="save()" tabindex="110" class="w-36" :disabled="isSaving"><i v-if="isSaving" class="p-button-icon pi pi-spinner pi-spin" /><span>{{ t('common.save') }}</span></Button>
		</template>

	</Dialog>
</template>

<script setup lang="ts">
import globalEvents from '@/utilities/GlobalEvents'
import EmployeePicture from '@/components/EmployeePicture.vue'
import PresenceIcon from '@/components/PresenceIcon.vue'
import { useI18n } from 'vue-i18n'
import type Employee from '@/stores/Employee'
import { useEmployeesStore } from '@/stores/Employees'
import { ref } from 'vue'
import { EmployeePresenceState } from '@/TcxApi/Employee'
import { useApiStore } from '@/stores/Api'
import type { AxiosError } from 'axios'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'
import GlyphIcon from '@/components/GlyphIcon.vue'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

const employees = useEmployeesStore()
const { t } = useI18n()
const api = useApiStore()

const expire = ref<{ enabled: boolean, date?: Date, min?: Date, max?: Date }>({ enabled: false, date: undefined, min: undefined, max: undefined })
const isSaving = ref(false)
const employee = ref<Employee | null>(null) // Our local copy of the employee, so that we can modify it without affecting the global state
const isVisible = ref(false)

const states = [
	{ key: EmployeePresenceState.AVAILABLE, label: t('common.presence.AVAILABLE') },
	{ key: EmployeePresenceState.BUSY, label: t('common.presence.BUSY') },
	{ key: EmployeePresenceState.OUT_OF_OFFICE, label: t('common.presence.OUT_OF_OFFICE') },
	{ key: EmployeePresenceState.OFF_WORK, label: t('common.presence.OFF_WORK') },
	{ key: EmployeePresenceState.HOLIDAY, label: t('common.presence.HOLIDAY') }
]

function keyDownHandler(event: KeyboardEvent) {
	if (event.key === 'Enter') { save() }
}

function show(target?: Employee) {
	if (!target && employees.me) { target = employees.me as unknown as Employee }
	if (!target) { return }

	employee.value = target.clone()

	if (employee.value?.presence?.expire) {
		const ex = new Date(employee.value.presence.expire)

		expire.value.enabled = true
		expire.value.date = ex
	} else {
		expire.value.enabled = false
		expire.value.date = undefined
	}
	expire.value.min = new Date()
	const max = new Date()

	max.setMonth(max.getMonth() + 12)
	expire.value.max = max

	isVisible.value = true
}

function close() {
	isVisible.value = false
}

function selectState(state: EmployeePresenceState) {
	if (employee.value?.presence) {
		employee.value.presence.state = state
		if (state == EmployeePresenceState.AVAILABLE) {
			expire.value.enabled = false
			expire.value.date = undefined
		}
	}
}

async function save() {
	if (!employee.value?.presence) { return }
	isSaving.value = true

	if (expire.value.enabled && expire.value.date) {
		employee.value.presence.expire = expire.value.date
	} else {
		employee.value.presence.expire = null
	}

	const presence = { presence: employee.value.presence }

	try {
		await api.post(`/employee/${employee.value._id}`, presence)
		isSaving.value = false
		close()
	}
	catch (err) {
		isSaving.value = false
		close()
		if ((err as AxiosError).status === 0) {
			globalEvents.emit('CommonErrorModal', { header: t('common.error.commErrorHeader'), message: t('common.error.commErrorMessage') })
		} else {
			const error = err as ErrorResponseDetail

			globalEvents.emit('CommonErrorModal', { header: t('setPresence.errorHeader'), message: t('setPresence.errorUnknown') + ` ${error.status} ${error.path}\n${error.message}` })
		}
	}
}

globalEvents.on('Auth:LoggedOut', close)
globalEvents.on('Realtime:Disconnected', close)

defineExpose({ show })
</script>

<style scoped>
	.state-box-item {
		width: 100px;
		height: 90px;
		display: flex;
		flex-direction: column;
		color: var(--p-text-color);
		font-size: 12px;
		border: 1px solid var(--p-primary-600);
		background-color: var(--p-primary-100);
	}
	.state-box-item:focus {
		/* border: 0; */
		box-shadow: 0 0 5px 2px rgba(0, 0, 0, 0.2) !important;
	}
	.unselected {
		opacity: 0.6;
		background-color: inherit;
		box-shadow: none;
		border: 1px solid var(--p-content-border-color);
	}
	.unselected:hover {
		opacity: 1;
	}
	@media(prefers-color-scheme: dark) {
		.state-box-item {
			background-color: var(--p-surface-950);
			border: 1px solid var(--p-surface-400);
		}
		.unselected {
			background-color: var(--p-surface-800);
			border: 1px solid var(--p-surface-900);
		}
	}
</style>
